import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getUsers, createUser, updateUser, deleteUser,
  getRoles, getPermissions, createRole, updateRole, 
  deleteRole, assignRoles, seedPermissions,
  getPasswordResetRequests, processPasswordResetRequest, deletePasswordResetRequest
} from '../services/api';
import Modal from '../components/Modal';
import ConfirmModal from '../components/Confirmmodal';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import '../styles/Pages.css';
import '../styles/Admin.css';
import { formatDateTime } from '../utils/formatters';


function Admin() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [pendingResetCount, setPendingResetCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  
  // Modal states
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [selectedResetRequest, setSelectedResetRequest] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  const [editingRole, setEditingRole] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Onay modalı state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {},
    confirmText: 'Evet',
    loading: false
  });
  
  const navigate = useNavigate();

  // Form states
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permission_ids: []
  });

  const [assignFormData, setAssignFormData] = useState({
    user_id: null,
    role_ids: []
  });

  const [userFormData, setUserFormData] = useState({
    username: '',
    full_name: '',
    email: '',  
    password: '',
    is_admin: false,
    is_active: true
  });

  useEffect(() => {
    checkAdminAccess();
    fetchData();
  }, []);

  const checkAdminAccess = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.is_admin) {
          toast.error('Bu sayfaya erişim yetkiniz yok');
          navigate('/dashboard');
        }
      } catch (e) {
        console.error('Token parse error:', e);
      }
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, permsRes, resetRes] = await Promise.all([
        getUsers(),
        getRoles(),
        getPermissions(),
        getPasswordResetRequests()
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
      setPasswordResetRequests(resetRes.data);
      
      const pendingCount = resetRes.data.filter(r => r.status === 'pending').length;
      setPendingResetCount(pendingCount);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Bu sayfaya erişim yetkiniz yok');
        navigate('/dashboard');
      } else {
        toast.error('Veri yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  
  const handleOpenUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserFormData({
        username: user.username,
        full_name: user.full_name || '',
        email: user.email || '',  
        password: '', 
        is_admin: user.is_admin,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setUserFormData({
        username: '',
        full_name: '',
        email: '',  
        password: '',
        is_admin: false,
        is_active: true
      });
    }
    setIsUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setEditingUser(null);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser && !userFormData.password) {
      toast.error('Yeni kullanıcı için şifre zorunludur');
      return;
    }

    const loadingToast = toast.loading(editingUser ? 'Güncelleniyor...' : 'Oluşturuluyor...');
    
    try {
      const submitData = { ...userFormData };
      
      if (editingUser && !submitData.password) {
        delete submitData.password;
      }
      
     
      if (!submitData.email) {
        submitData.email = null;
      }

      if (editingUser) {
        await updateUser(editingUser.id, submitData);
        toast.success('Kullanıcı güncellendi!', { id: loadingToast });
      } else {
        await createUser(submitData);
        toast.success('Kullanıcı oluşturuldu!', { id: loadingToast });
      }
      handleCloseUserModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = error.response?.data?.detail || 'İşlem başarısız';
      toast.error(errorMsg, { id: loadingToast });
    }
  };

  // Kullanıcı aktif/pasif - onay modalı
  const handleToggleUserActiveClick = (user) => {
    const action = user.is_active ? 'pasif' : 'aktif';
    const type = user.is_active ? 'warning' : 'info';
    
    setConfirmModal({
      isOpen: true,
      title: user.is_active ? 'Hesabı Pasif Yap' : 'Hesabı Aktif Yap',
      message: `"${user.username}" kullanıcısını ${action} yapmak istediğinizden emin misiniz?${user.is_active ? ' Kullanıcı sisteme giriş yapamayacak!' : ''}`,
      type: type,
      confirmText: `Evet, ${action.charAt(0).toUpperCase() + action.slice(1)} Yap`,
      loading: false,
      onConfirm: () => handleToggleUserActiveConfirm(user)
    });
  };

  const handleToggleUserActiveConfirm = async (user) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    const action = user.is_active ? 'pasif' : 'aktif';
    
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      toast.success(`Kullanıcı ${action} yapıldı`);
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Hata oluştu');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Kullanıcı silme - onay modalı
  const handleDeleteUserClick = (user) => {
    if (user.is_admin) {
      toast.error('Admin kullanıcı silinemez');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Kullanıcı Silme',
      message: `"${user.username}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`,
      type: 'danger',
      confirmText: 'Evet, Sil',
      loading: false,
      onConfirm: () => handleDeleteUserConfirm(user.id)
    });
  };

  const handleDeleteUserConfirm = async (id) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    
    try {
      await deleteUser(id);
      toast.success('Kullanıcı silindi');
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Silme hatası');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSeedPermissions = async () => {
    const loadingToast = toast.loading('İzinler oluşturuluyor...');
    try {
      await seedPermissions();
      toast.success('Varsayılan izinler oluşturuldu', { id: loadingToast });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Hata oluştu', { id: loadingToast });
    }
  };

  const handleOpenRoleModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleFormData({
        name: role.name,
        description: role.description || '',
        permission_ids: role.permissions.map(p => p.id)
      });
    } else {
      setEditingRole(null);
      setRoleFormData({
        name: '',
        description: '',
        permission_ids: []
      });
    }
    setIsRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setIsRoleModalOpen(false);
    setEditingRole(null);
  };

  const handleSubmitRole = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingRole ? 'Güncelleniyor...' : 'Oluşturuluyor...');
    
    try {
      if (editingRole) {
        await updateRole(editingRole.id, roleFormData);
        toast.success('Rol güncellendi!', { id: loadingToast });
      } else {
        await createRole(roleFormData);
        toast.success('Rol oluşturuldu!', { id: loadingToast });
      }
      handleCloseRoleModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız', { id: loadingToast });
    }
  };

  // Rol silme - onay modalı
  const handleDeleteRoleClick = (role) => {
    setConfirmModal({
      isOpen: true,
      title: 'Rol Silme',
      message: `"${role.name}" rolünü silmek istediğinizden emin misiniz? Bu role sahip kullanıcıların yetkileri etkilenecektir!`,
      type: 'danger',
      confirmText: 'Evet, Sil',
      loading: false,
      onConfirm: () => handleDeleteRoleConfirm(role.id)
    });
  };

  const handleDeleteRoleConfirm = async (id) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    
    try {
      await deleteRole(id);
      toast.success('Rol silindi');
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Silme hatası');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleOpenResetModal = (request) => {
    setSelectedResetRequest(request);
    setNewPassword('');
    setIsResetPasswordModalOpen(true);
  };

  const handleCloseResetModal = () => {
    setIsResetPasswordModalOpen(false);
    setSelectedResetRequest(null);
    setNewPassword('');
  };

  const handleApproveReset = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Şifre en az 4 karakter olmalı');
      return;
    }

    const loadingToast = toast.loading('Şifre sıfırlanıyor...');
    try {
      await processPasswordResetRequest(selectedResetRequest.id, {
        action: 'complete',
        new_password: newPassword
      });
      toast.success('Şifre sıfırlandı!', { id: loadingToast });
      handleCloseResetModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Hata oluştu', { id: loadingToast });
    }
  };

  const handleRejectReset = async (request) => {
    setConfirmModal({
      isOpen: true,
      title: 'Talebi Reddet',
      message: `"${request.username}" kullanıcısının şifre sıfırlama talebini reddetmek istediğinizden emin misiniz?`,
      type: 'warning',
      confirmText: 'Evet, Reddet',
      loading: false,
      onConfirm: () => handleRejectResetConfirm(request.id)
    });
  };

  const handleRejectResetConfirm = async (id) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    
    try {
      await processPasswordResetRequest(id, {
        action: 'reject'
      });
      toast.success('Talep reddedildi');
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Hata oluştu');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteResetRequest = async (request) => {
    setConfirmModal({
      isOpen: true,
      title: 'Talebi Sil',
      message: `Bu şifre sıfırlama talebini silmek istediğinizden emin misiniz?`,
      type: 'danger',
      confirmText: 'Evet, Sil',
      loading: false,
      onConfirm: () => handleDeleteResetConfirm(request.id)
    });
  };

  const handleDeleteResetConfirm = async (id) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    
    try {
      await deletePasswordResetRequest(id);
      toast.success('Talep silindi');
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Silme hatası');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning">Bekliyor</span>;
      case 'completed':
        return <span className="badge badge-success">Tamamlandı</span>;
      case 'rejected':
        return <span className="badge badge-danger">Reddedildi</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const handleOpenAssignModal = (user) => {
    setSelectedUser(user);
    setAssignFormData({
      user_id: user.id,
      role_ids: user.roles?.map(r => r.id) || []
    });
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedUser(null);
  };

  const handleSubmitAssign = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Roller atanıyor...');
    
    try {
      await assignRoles(assignFormData);
      toast.success('Roller atandı!', { id: loadingToast });
      handleCloseAssignModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız', { id: loadingToast });
    }
  };

  const togglePermission = (permId) => {
    setRoleFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permId)
        ? prev.permission_ids.filter(id => id !== permId)
        : [...prev.permission_ids, permId]
    }));
  };

  const toggleRole = (roleId) => {
    setAssignFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <div className="page-container">
      <Navbar />

      <div className="page-content">
        <h2>Admin Paneli</h2>

        <div className="admin-tabs">
          <button 
            className={activeTab === 'users' ? 'active' : ''} 
            onClick={() => setActiveTab('users')}
          >
             Kullanıcılar
          </button>
          <button 
            className={activeTab === 'roles' ? 'active' : ''} 
            onClick={() => setActiveTab('roles')}
          >
             Roller
          </button>
          <button 
            className={activeTab === 'permissions' ? 'active' : ''} 
            onClick={() => setActiveTab('permissions')}
          >
             İzinler
          </button>
          <button 
            className={activeTab === 'password-requests' ? 'active' : ''} 
            onClick={() => setActiveTab('password-requests')}
          >
             Şifre Talepleri
            {pendingResetCount > 0 && (
              <span className="badge-count">{pendingResetCount}</span>
            )}
          </button>
        </div>

        {/* Kullanıcılar Tab */}
        {activeTab === 'users' && (
          <div className="tab-content">
            <div className="page-header">
              <h3>Kullanıcı Yönetimi</h3>
              <button className="btn-primary" onClick={() => handleOpenUserModal()}>
                + Yeni Kullanıcı
              </button>
            </div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Kullanıcı Adı</th>
                    <th>Ad Soyad</th>
                    <th>Email</th>
                    <th>Roller</th>
                    <th>Yetki</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className={!user.is_active ? 'row-inactive' : ''}>
                      <td title={`#${user.id}`}><strong>{user.username}</strong></td>
                      <td>{user.full_name || '-'}</td>
                      <td>
                        {user.email ? (
                          <span style={{color: '#667eea', fontSize: '13px'}}>{user.email}</span>
                        ) : (
                          <span style={{color: '#999', fontSize: '12px'}}>-</span>
                        )}
                      </td>
                      <td>
                        <div className="role-tags">
                          {user.roles?.length > 0 ? (
                            user.roles.map(r => (
                              <span key={r.id} className="role-tag">{r.name}</span>
                            ))
                          ) : (
                            <span className="no-role">Rol atanmamış</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${user.is_admin ? 'badge-admin' : 'badge-user'}`}>
                          {user.is_admin ? 'Admin' : 'Kullanıcı'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {user.is_active ? '✓ Aktif' : '✗ Pasif'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-edit"
                            onClick={() => handleOpenUserModal(user)}
                            title="Düzenle"
                          >
                            ✎
                          </button>
                          <button 
                            className="btn-role"
                            onClick={() => handleOpenAssignModal(user)}
                            title="Rol Ata"
                          >
                            🎭
                          </button>
                          <button 
                            className={`btn-toggle ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                            onClick={() => handleToggleUserActiveClick(user)}
                            title={user.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                          >
                            {user.is_active ? '⏸' : '▶'}
                          </button>
                          {!user.is_admin && (
                            <button 
                              className="btn-delete"
                              onClick={() => handleDeleteUserClick(user)}
                              title="Sil"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                            </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="info-box">
              <h4>ℹ️ Bilgi</h4>
              <ul>
                <li><strong>Admin:</strong> Tüm sayfalara ve işlemlere erişebilir</li>
                <li><strong>Kullanıcı:</strong> Sadece izin verilen sayfalara erişebilir</li>
                <li><strong>Pasif:</strong> Sisteme giriş yapamaz</li>
                <li><strong>Email:</strong> Email ile şifre sıfırlama için gerekli</li>
              </ul>
            </div>
          </div>
        )}

        {/* Roller Tab */}
        {activeTab === 'roles' && (
          <div className="tab-content">
            <div className="page-header">
              <h3>Rol Yönetimi</h3>
              <button className="btn-primary" onClick={() => handleOpenRoleModal()}>
                + Yeni Rol
              </button>
            </div>

            <div className="roles-grid">
              {roles.length === 0 ? (
                <div className="empty-state">
                  <p>Henüz rol tanımlanmamış</p>
                  <button className="btn-primary" onClick={() => handleOpenRoleModal()}>
                    İlk Rolü Oluştur
                  </button>
                </div>
              ) : (
                roles.map(role => (
                  <div key={role.id} className="role-card">
                    <div className="role-header">
                      <h4>{role.name}</h4>
                      <div className="role-actions">
                        <button 
                          className="btn-edit"
                          onClick={() => handleOpenRoleModal(role)}
                        >
                          ✎
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDeleteRoleClick(role)}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="role-description">{role.description || 'Açıklama yok'}</p>
                    <div className="role-permissions">
                      <strong>İzinler ({role.permissions?.length || 0}):</strong>
                      {role.permissions?.length > 0 ? (
                        <ul>
                          {role.permissions.map(p => (
                            <li key={p.id}>{p.description || p.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-permissions">İzin atanmamış</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* İzinler Tab */}
        {activeTab === 'permissions' && (
          <div className="tab-content">
            <div className="page-header">
              <h3>İzin Yönetimi</h3>
              <button className="btn-primary" onClick={handleSeedPermissions}>
                🔄 Varsayılan İzinleri Oluştur
              </button>
            </div>

            {permissions.length === 0 ? (
              <div className="empty-state">
                <p>Henüz izin tanımlanmamış</p>
                <p className="hint">Yukarıdaki butona tıklayarak varsayılan izinleri oluşturabilirsiniz</p>
              </div>
            ) : (
              <div className="permissions-grid">
                {permissions.map(perm => (
                  <div key={perm.id} className="permission-card">
                    <div className="permission-icon">🔑</div>
                    <div className="permission-info">
                      <h4>{perm.name}</h4>
                      <p>{perm.description || 'Açıklama yok'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Şifre Talepleri Tab */}
        {activeTab === 'password-requests' && (
          <div className="tab-content">
            <div className="page-header">
              <h3>Şifre Sıfırlama Talepleri</h3>
              <div className="header-info">
                {pendingResetCount > 0 && (
                  <span className="pending-badge">
                    {pendingResetCount} bekleyen talep
                  </span>
                )}
              </div>
            </div>

            {passwordResetRequests.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <p>Şifre sıfırlama talebi bulunmuyor</p>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Kullanıcı</th>
                      <th>Durum</th>
                      <th>Talep Tarihi</th>
                      <th>İşlem Tarihi</th>
                      <th>İşleyen</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passwordResetRequests.map(request => (
                      <tr key={request.id} className={request.status === 'pending' ? 'row-pending' : ''}>
                        <td title={`#${request.id}`}><strong>{request.username}</strong></td>
                        <td>{getStatusBadge(request.status)}</td>
                        <td>{formatDateTime(request.created_at)}</td>
                        <td>{formatDateTime(request.processed_at)}</td>
                        <td>{request.processed_by || '-'}</td>
                        <td>
                          <div className="action-buttons">
                            {request.status === 'pending' && (
                              <>
                                <button 
                                  className="btn-success"
                                  onClick={() => handleOpenResetModal(request)}
                                  title="Onayla ve Şifre Belirle"
                                >
                                  ✓
                                </button>
                                <button 
                                  className="btn-warning"
                                  onClick={() => handleRejectReset(request)}
                                  title="Reddet"
                                >
                                  ✕
                                </button>
                              </>
                            )}
                            <button 
                              className="btn-delete"
                              onClick={() => handleDeleteResetRequest(request)}
                              title="Sil"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                <path d="M10 11v6M14 11v6"/>
                                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Kullanıcı Oluştur/Düzenle Modal */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={handleCloseUserModal}
        title={editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
        size="medium"
      >
        <form onSubmit={handleSubmitUser}>
          <div className="form-group">
            <label>Kullanıcı Adı *</label>
            <input
              type="text"
              value={userFormData.username}
              onChange={(e) => setUserFormData({...userFormData, username: e.target.value})}
              required
              placeholder="ornek_kullanici"
              disabled={editingUser} 
            />
            {editingUser && (
              <small className="form-hint">Kullanıcı adı değiştirilemez</small>
            )}
          </div>

          <div className="form-group">
            <label>Ad Soyad</label>
            <input
              type="text"
              value={userFormData.full_name}
              onChange={(e) => setUserFormData({...userFormData, full_name: e.target.value})}
              placeholder=""
            />
          </div>

          {/* YENİ: Email Alanı */}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={userFormData.email}
              onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
              placeholder="ornek@email.com"
            />
            <small className="form-hint">Email ile şifre sıfırlama için gerekli</small>
          </div>

          <div className="form-group">
            <label>{editingUser ? 'Yeni Şifre' : 'Şifre *'}</label>
            <input
              type="password"
              value={userFormData.password}
              onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
              required={!editingUser}
              placeholder={editingUser ? 'Değiştirmek istemiyorsanız boş bırakın' : 'Güçlü bir şifre girin'}
              minLength="4"
            />
            {editingUser && (
              <small className="form-hint">Boş bırakırsanız şifre değişmez</small>
            )}
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label className="checkbox-label-inline" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={userFormData.is_admin}
                  onChange={(e) => setUserFormData({...userFormData, is_admin: e.target.checked})}
                />
                <span>Admin Yetkisi</span>
              </label>
              <small className="form-hint" style={{ display: 'block', marginLeft: '25px', marginTop: '-5px' }}>
                Admin tüm sayfa ve işlemlere erişebilir
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label-inline" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={userFormData.is_active}
                  onChange={(e) => setUserFormData({...userFormData, is_active: e.target.checked})}
                />
                <span>Aktif Hesap</span>
              </label>
              <small className="form-hint" style={{ display: 'block', marginLeft: '25px', marginTop: '-5px' }}>
                Pasif hesaplar giriş yapamaz
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCloseUserModal}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              {editingUser ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Rol Oluştur/Düzenle Modal */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={handleCloseRoleModal}
        title={editingRole ? 'Rol Düzenle' : 'Yeni Rol'}
        size="large"
      >
        <form onSubmit={handleSubmitRole}>
          <div className="form-group">
            <label>Rol Adı *</label>
            <input
              type="text"
              value={roleFormData.name}
              onChange={(e) => setRoleFormData({...roleFormData, name: e.target.value})}
              required
              placeholder="Örn: Satış Temsilcisi"
            />
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={roleFormData.description}
              onChange={(e) => setRoleFormData({...roleFormData, description: e.target.value})}
              rows="2"
              placeholder="Bu rolün ne işe yaradığını açıklayın"
            />
          </div>

          <div className="form-group">
            <label>İzinler</label>
            {permissions.length === 0 ? (
              <p className="no-permissions">
                Henüz izin tanımlanmamış. Önce "İzinler" sekmesinden varsayılan izinleri oluşturun.
              </p>
            ) : (
              <div className="checkbox-grid">
                {permissions.map(perm => (
                  <label key={perm.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={roleFormData.permission_ids.includes(perm.id)}
                      onChange={() => togglePermission(perm.id)}
                    />
                    {perm.description || perm.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCloseRoleModal}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              {editingRole ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Rol Atama Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        title={`${selectedUser?.username} - Rol Atama`}
        size="medium"
      >
        <form onSubmit={handleSubmitAssign}>
          <div className="user-info-box">
            <p><strong>Kullanıcı:</strong> {selectedUser?.username}</p>
            <p><strong>Ad Soyad:</strong> {selectedUser?.full_name || '-'}</p>
          </div>

          <div className="form-group">
            <label>Atanacak Roller</label>
            {roles.length === 0 ? (
              <p className="no-permissions">
                Henüz rol tanımlanmamış. Önce "Roller" sekmesinden rol oluşturun.
              </p>
            ) : (
              <div className="checkbox-grid">
                {roles.map(role => (
                  <label key={role.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={assignFormData.role_ids.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <div>
                      <strong>{role.name}</strong>
                      {role.description && <small> - {role.description}</small>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCloseAssignModal}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </Modal>

      {/* Şifre Sıfırlama Modalı */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={handleCloseResetModal}
        title="Şifre Sıfırla"
        size="small"
      >
        <div className="reset-password-modal">
          <div className="reset-info-box">
            <p><strong>Kullanıcı:</strong> {selectedResetRequest?.username}</p>
            <p><strong>Talep Tarihi:</strong> {formatDateTime(selectedResetRequest?.created_at)}</p>
          </div>

          <div className="form-group">
            <label>Yeni Şifre *</label>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Yeni şifreyi girin"
              autoFocus
            />
            <small className="form-hint">Kullanıcıya bu şifreyi iletmeyi unutmayın</small>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCloseResetModal}>
              İptal
            </button>
            <button
              className="btn btn-primary"
              onClick={handleApproveReset}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Şifreyi Sıfırla
            </button>
          </div>
        </div>
      </Modal>

      {/* Onay Modalı */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        loading={confirmModal.loading}
      />
    </div>
  );
}

export default Admin;